import { useQuery } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';
import type { Components } from 'react-markdown';
import { Link, useParams } from 'react-router';

import { MarkdownRenderer } from '@/features/chat/MarkdownRenderer';
import { SectionWrapper } from '@/features/marketing/components/SectionWrapper';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import { SupportFigure } from './SupportFigure';
import { SupportLink } from './SupportLink';
import { DraftNotes } from './supportDrafts';
import {
  findEntry,
  findStage,
  indexPath,
  indexTitle,
  isPublished,
  loadArticle,
  type SupportEntry,
} from './supportContent';

import styles from './SupportArticlePage.module.css';

function SupportNotFound() {
  useDocumentTitle('Help');

  return (
    <SectionWrapper background="white" className={styles.section}>
      <div className={styles.article}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Help</p>
          <h1 className={styles.title}>Page not found</h1>
        </header>
        <p className={styles.lead}>There is no help article at this address.</p>
        <p className={styles.lead}>
          <Link className={styles.link} to={indexPath('student')}>
            {indexTitle('student')}
          </Link>
          {' · '}
          <Link className={styles.link} to={indexPath('instructor')}>
            {indexTitle('instructor')}
          </Link>
        </p>
      </div>
    </SectionWrapper>
  );
}

function ArticleBody({
  components,
  entry,
}: {
  components: Partial<Components>;
  entry: SupportEntry;
}) {
  const article = useQuery({
    queryFn: () => loadArticle(entry),
    queryKey: ['support-article', entry.slug],
    retry: false,
    staleTime: Infinity,
  });

  if (article.isPending) {
    return (
      <p className={styles.status} role="status">
        Loading…
      </p>
    );
  }

  if (article.isError) {
    return (
      <p className={styles.status} role="note">
        This article has not been written yet.
      </p>
    );
  }

  return (
    <div className={styles.body}>
      <MarkdownRenderer components={components} content={article.data} />
    </div>
  );
}

export function SupportArticlePage() {
  const { slug = '' } = useParams();
  const entry = findEntry(slug);
  const visible = Boolean(entry && isPublished(entry));

  useDocumentTitle(entry && visible ? entry.title : 'Help');

  const components = useMemo<Partial<Components>>(
    () =>
      entry
        ? {
            a: SupportLink,
            img: ({ src }) => <SupportFigure entry={entry} src={src} />,
          }
        : {},
    [entry],
  );

  if (!entry || !visible) {
    return <SupportNotFound />;
  }

  const stage = findStage(entry.stage);
  const audienceLabel = entry.audience === 'instructor' ? 'Instructors' : 'Students';

  return (
    <SectionWrapper background="white" className={styles.section}>
      <article className={styles.article}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>
            <Link className={styles.eyebrowLink} to={indexPath(entry.audience)}>
              {indexTitle(entry.audience)}
            </Link>{' '}
            <span aria-hidden="true">·</span>{' '}
            <span className={styles.eyebrowStage}>
              {audienceLabel}
              {stage ? ` · ${stage.title}` : ''}
            </span>
          </p>
          <h1 className={styles.title}>{entry.title}</h1>
        </header>

        {DraftNotes ? (
          <Suspense fallback={null}>
            <DraftNotes entry={entry} kind="banner" />
          </Suspense>
        ) : null}

        <ArticleBody components={components} entry={entry} />

        <footer className={styles.footer}>
          <Link className={styles.link} to={indexPath(entry.audience)}>
            All {entry.audience} help
          </Link>
        </footer>
      </article>
    </SectionWrapper>
  );
}
